import React, { useMemo, useState } from 'react';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const COLORS = ['#6366F1', '#0EA5E9', '#22C55E', '#F97316', '#EC4899', '#14B8A6', '#FBBF24', '#8B5CF6', '#EF4444', '#3B82F6'];

const SVG_HEIGHT = 280;
const MARGIN = { top: 28, right: 24, bottom: 56, left: 52 };
const GROUP_GAP = 10;
const BAR_GAP = 3;
const BAR_WIDTH = 13;

const TeacherDeparturesPerMonthChart = ({ data }) => {
  const matrix = useMemo(() => {
    if (!Array.isArray(data) || data.length === 0) {
      return {
        years: [],
        groups: [],
        maxCount: 0,
        totalDepartures: 0
      };
    }

    const years = Array.from(
      new Set(data.map((item) => item.date.getFullYear()))
    ).sort((a, b) => a - b);

    const dataMap = new Map(
      data.map((item) => [`${item.date.getFullYear()}-${item.date.getMonth()}`, item])
    );

    const groups = MONTH_LABELS.map((label, monthIdx) => {
      const entries = years.map((year) => {
        const key = `${year}-${monthIdx}`;
        const record = dataMap.get(key);
        return {
          year,
          count: record ? record.count : 0,
          hasData: Boolean(record)
        };
      });
      const hasAny = entries.some((entry) => entry.hasData);
      return {
        label,
        monthIdx,
        entries,
        hasAny
      };
    }).filter((group) => group.hasAny);

    const maxCount = groups.reduce(
      (acc, group) => Math.max(acc, ...group.entries.map((entry) => entry.count)),
      0
    );

    const totalDepartures = data.reduce((sum, item) => sum + (item.count || 0), 0);

    return {
      years,
      groups,
      maxCount,
      totalDepartures
    };
  }, [data]);

  const [tooltip, setTooltip] = useState({
    show: false,
    x: 0,
    monthLabel: '',
    year: 0,
    count: 0
  });

  if (!Array.isArray(data) || data.length === 0) {
    return <div className="text-sm text-slate-500">No teacher departure data available yet.</div>;
  }

  const { years, groups, maxCount, totalDepartures } = matrix;
  if (!groups.length || !years.length || maxCount === 0) {
    return <div className="text-sm text-slate-500">No teacher departure data available yet.</div>;
  }

  const avgDepartures = Math.round(totalDepartures / data.length);
  const latestYear = years[years.length - 1];
  const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const groupWidth = years.length * BAR_WIDTH + Math.max(0, years.length - 1) * BAR_GAP;
  const chartWidth = MARGIN.left + MARGIN.right + groups.length * groupWidth + Math.max(0, groups.length - 1) * GROUP_GAP;

  const handleHideTooltip = () => {
    setTooltip((prev) => (prev.show ? { ...prev, show: false } : prev));
  };

  const handleShowTooltip = (groupIdx, yearIdx, entry, groupLabel) => {
    const groupX = MARGIN.left + groupIdx * (groupWidth + GROUP_GAP);
    const barCenterX = groupX + yearIdx * (BAR_WIDTH + BAR_GAP) + BAR_WIDTH / 2;
    setTooltip({
      show: true,
      x: barCenterX,
      monthLabel: groupLabel,
      year: entry.year,
      count: entry.count
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600">
        <div className="flex items-baseline gap-1">
          <span className="uppercase tracking-wide text-xs text-slate-500">Years Covered:</span>
          <span className="text-slate-900 font-semibold">{years[0]} – {years[years.length - 1]}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="uppercase tracking-wide text-xs text-slate-500">Total Departures:</span>
          <span className="text-slate-900 font-semibold">{totalDepartures.toLocaleString()}</span>
        </div>
        <div className="flex items-baseline gap-1">
          <span className="uppercase tracking-wide text-xs text-slate-500">Average per Month:</span>
          <span className="text-slate-900 font-semibold">{avgDepartures.toLocaleString()}</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        {years.map((year, idx) => (
          <span key={year} className="inline-flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: COLORS[idx % COLORS.length], opacity: year === latestYear ? 1 : 0.7 }}
            />
            <span className={year === latestYear ? 'font-semibold text-slate-800' : ''}>{year}</span>
          </span>
        ))}
      </div>

      <div className="relative overflow-x-auto">
        <svg
          width={chartWidth}
          height={SVG_HEIGHT}
          role="img"
          aria-label="Teacher departures per month grouped by year"
          className="text-xs"
          onMouseLeave={handleHideTooltip}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((t, idx) => {
            const value = Math.round(maxCount * t);
            const y = MARGIN.top + innerHeight * (1 - t);
            return (
              <g key={idx}>
                <line
                  x1={MARGIN.left}
                  x2={chartWidth - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke="rgba(148, 163, 184, 0.2)"
                />
                <text
                  x={MARGIN.left - 8}
                  y={y}
                  textAnchor="end"
                  dominantBaseline="middle"
                  fill="#94A3B8"
                >
                  {value.toLocaleString()}
                </text>
              </g>
            );
          })}

          {groups.map((group, groupIdx) => {
            const groupX = MARGIN.left + groupIdx * (groupWidth + GROUP_GAP);
            return (
              <g key={group.monthIdx} transform={`translate(${groupX}, 0)`}>
                {group.entries.map((entry, yearIdx) => {
                  const barHeight = maxCount ? (entry.count / maxCount) * innerHeight : 0;
                  const x = yearIdx * (BAR_WIDTH + BAR_GAP);
                  const y = MARGIN.top + innerHeight - barHeight;
                  return (
                    <g key={`${entry.year}-${group.monthIdx}`}>
                      <rect
                        x={x}
                        y={y}
                        width={BAR_WIDTH}
                        height={barHeight}
                        rx={5}
                        fill={COLORS[yearIdx % COLORS.length]}
                        fillOpacity={entry.year === latestYear ? 1 : 0.7}
                      />
                      <rect
                        x={x - BAR_GAP / 2}
                        y={MARGIN.top}
                        width={BAR_WIDTH + BAR_GAP}
                        height={innerHeight}
                        fill="transparent"
                        tabIndex={0}
                        aria-label={`${group.label} ${entry.year}: ${entry.count.toLocaleString()} teacher departures`}
                        onMouseEnter={() => handleShowTooltip(groupIdx, yearIdx, entry, group.label)}
                        onMouseMove={() => handleShowTooltip(groupIdx, yearIdx, entry, group.label)}
                        onMouseLeave={handleHideTooltip}
                        onFocus={() => handleShowTooltip(groupIdx, yearIdx, entry, group.label)}
                        onBlur={handleHideTooltip}
                      />
                    </g>
                  );
                })}

                <text
                  x={groupWidth / 2}
                  y={SVG_HEIGHT - MARGIN.bottom + 20}
                  textAnchor="middle"
                  fill="#475569"
                  className="font-medium"
                >
                  {group.label}
                </text>
              </g>
            );
          })}
        </svg>
        {tooltip.show && (
          <div
            className="pointer-events-none absolute -translate-x-1/2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-md text-slate-700"
            style={{
              left: `${Math.min(Math.max(tooltip.x, 60), chartWidth - 60)}px`,
              top: `8px`
            }}
          >
            <div className="font-semibold text-slate-900">{tooltip.monthLabel} {tooltip.year}</div>
            <div className="mt-1 text-slate-600 tabular-nums">{tooltip.count.toLocaleString()} departures</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherDeparturesPerMonthChart;
